// challenge-replay — the shared record/replay engine behind the Code Challenge
// "Record Answer Key" (teacher) and "Check" (student) flows. ONE mechanism for
// all three languages (decisions.md): console Python replays a recorded stdin
// sequence; Pygame Zero/plain Pygame replay a recorded keyboard/mouse event
// sequence, paced at the events' original relative `atMs` offsets.
//
// Neither capture nor replay adds a new runner postMessage type:
//   - Capture listens on the runner iframe's OWN canvas (same-origin) with the
//     exact same math runner-core.js's own wireInput() uses, so the compact
//     record shape matches what the worker already accepts.
//   - Replay for keyboard/mouse calls `window.__runnerCore.injectInput(rec)`
//     directly (same-origin property access, not a message) — the SAME
//     function (`sendInput`) the runner's real DOM listeners already call.
//   - Replay for console stdin posts the EXISTING `runner:stdin` message.
//
// Both record() and replay() are bounded by the same 15-second watchdog
// (decisions.md "same shared constant" — round 3 + task 2.5).

import { FRAME_DT } from './challenge-grading.js';

export const WATCHDOG_MS = 15000;
// Small fixed grace period after the last recorded event's atMs before a
// replay is considered "done" for games that never signal `finished` on their
// own (pgzero always; plain Pygame often) — decisions.md task 2.5.
const REPLAY_GRACE_MS = 600;

// live-frame-index-tracking: given a recorded event's session-relative `atMs`
// and the accumulated list of `{frame, atMs}` live-frame checkpoints (also
// session-relative), find the last checkpoint at-or-before the event and
// return an accurate frame number — the checkpoint's own frame plus the
// fixed-rate-converted RESIDUAL gap since that checkpoint (bounded to
// ≲one checkpoint interval), rather than the whole session's elapsed time.
// Returns undefined (no `frame` field attached) when no checkpoint precedes
// the event — the event keeps only its existing `atMs`, and every downstream
// consumer falls back to today's plain fixed-rate conversion for that one
// event.
function computeAccurateFrame(eventAtMs, checkpoints) {
  var best = null;
  for (var i = 0; i < checkpoints.length; i++) {
    var cp = checkpoints[i];
    if (cp.atMs <= eventAtMs && (best === null || cp.atMs > best.atMs)) {
      best = cp;
    }
  }
  if (best === null) { return undefined; }
  return best.frame + Math.round((eventAtMs - best.atMs) / (1000 * FRAME_DT));
}

function canvasOf(core) {
  var frame = core.getRunnerFrame();
  var doc = frame && frame.contentDocument;
  return doc ? doc.getElementById('canvas') : null;
}

function runnerCoreOf(core) {
  var frame = core.getRunnerFrame();
  return frame && frame.contentWindow && frame.contentWindow.__runnerCore;
}

// Start capturing the reference solution's real input while it runs.
// `mode` is editor-core's clientsideMode ('' pgzero, 'pygame', 'python').
// Returns { stop() } — stop() detaches everything and returns
// { recordedInputs }. `opts.onTimeout` fires once at the 15s watchdog (the
// caller decides what "stop recording" means at that point — see
// challenge-record-chrome.js).
export function startRecording(core, mode, opts) {
  opts = opts || {};
  var onTimeout = opts.onTimeout || function () {};
  var events = [];
  var startedAt = performance.now();
  var stopped = false;
  var watchdogTimer = null;
  var stdinHandler = null;
  var stateHandler = null;
  var frameCheckpointHandler = null;
  var canvas = null;
  var handlers = null;
  // live-frame-index-tracking: accumulated {frame, atMs} pairs from the live
  // loop's periodic runner:live-frame-checkpoint reports, converted to the
  // SAME session-relative atMs() form the recorder's own events use.
  var frameCheckpoints = [];

  function atMs() { return Math.round(performance.now() - startedAt); }

  // Attach the keyboard/mouse listeners to whatever canvas is CURRENT right
  // now. Every run() call (including the very first) replaces the runner
  // iframe's <canvas> with a fresh element (freshCanvas() in runner-core.js —
  // an OffscreenCanvas transfer is one-shot) and wires the runner's OWN input
  // listeners onto that new node. Grabbing canvasOf(core) synchronously when
  // recording starts — i.e. right after core.runWithChallengeSeed() posts the
  // (async) load/run messages — captures the OLD node a moment before the
  // runner detaches and replaces it, so the recording listeners end up on a
  // dead element that never sees another event again: gameplay visibly works
  // (the runner's own listeners are on the new node) but recordedInputs stays
  // empty forever. Fix: defer attaching until the 'running' state fires —
  // which the runner only reaches AFTER freshCanvas()/wireInput() have run
  // (setState("running") follows a "program-starting" message from the fresh
  // worker) — the exact same anchor replay()'s beginReplayPhase() already
  // uses below for the analogous boot-timing race.
  function attachToCurrentCanvas() {
    canvas = canvasOf(core);
    if (!canvas) { return; }
    var mouseRecord = function (kind, e) {
      var r = canvas.getBoundingClientRect();
      return {
        kind: kind,
        x: e.clientX - r.left - canvas.clientLeft,
        y: e.clientY - r.top - canvas.clientTop,
        dx: e.movementX || 0,
        dy: e.movementY || 0,
        cw: canvas.clientWidth,
        ch: canvas.clientHeight,
        button: e.button,
        buttons: e.buttons,
        atMs: atMs()
      };
    };
    var keyRecord = function (kind, e) {
      return {
        kind: kind, code: e.code, key: e.key,
        shift: e.shiftKey, ctrl: e.ctrlKey, alt: e.altKey, meta: e.metaKey,
        atMs: atMs()
      };
    };
    handlers = {
      mousedown: function (e) { events.push(mouseRecord('mousedown', e)); },
      mouseup: function (e) { events.push(mouseRecord('mouseup', e)); },
      mousemove: function (e) { events.push(mouseRecord('mousemove', e)); },
      keydown: function (e) { if (!e.repeat) { events.push(keyRecord('keydown', e)); } },
      keyup: function (e) { events.push(keyRecord('keyup', e)); }
    };
    Object.keys(handlers).forEach(function (type) {
      canvas.addEventListener(type, handlers[type]);
    });
  }

  if (mode === 'python') {
    stdinHandler = function (text) {
      events.push({ kind: 'stdin', text: text, atMs: atMs() });
    };
    core.on('stdin-submit', stdinHandler);
  } else {
    stateHandler = function (state) {
      if (state === 'running' && !canvas) { attachToCurrentCanvas(); }
    };
    core.on('state', stateHandler);

    // live-frame-index-tracking: accumulate the live loop's periodic real
    // frame-count reports (emitted by editor-core.js with an ABSOLUTE
    // performance.now() reading, since it has no notion of this session's
    // startedAt) — convert to session-relative form here, the same way every
    // other recorded event already is via atMs().
    frameCheckpointHandler = function (msg) {
      frameCheckpoints.push({ frame: msg.frame, atMs: Math.round(msg.atMs - startedAt) });
    };
    core.on('frame-checkpoint', frameCheckpointHandler);
  }

  watchdogTimer = setTimeout(function () {
    if (!stopped) { onTimeout(); }
  }, WATCHDOG_MS);

  return {
    stop: function () {
      if (stopped) { return { recordedInputs: events }; }
      stopped = true;
      clearTimeout(watchdogTimer);
      if (stdinHandler) { core.off('stdin-submit', stdinHandler); }
      if (stateHandler) { core.off('state', stateHandler); }
      if (frameCheckpointHandler) { core.off('frame-checkpoint', frameCheckpointHandler); }
      if (canvas && handlers) {
        Object.keys(handlers).forEach(function (type) {
          canvas.removeEventListener(type, handlers[type]);
        });
      }
      // live-frame-index-tracking: attach an accurate `frame` field to every
      // recorded event (keyboard AND mouse — never just keyboard, which would
      // introduce new inter-event-type drift) where a preceding checkpoint
      // exists; events before the first checkpoint keep only `atMs` and every
      // downstream consumer falls back gracefully.
      events.forEach(function (rec) {
        var frame = computeAccurateFrame(rec.atMs || 0, frameCheckpoints);
        if (frame !== undefined) { rec.frame = frame; }
      });
      return { recordedInputs: events };
    }
  };
}

// Replay a recorded input sequence against whatever is currently in the live
// editor buffer, seeded deterministically. Resolves with
// `{ outcome: 'finished' | 'stopped' | 'error' }` — never rejects.
//   - 'finished': the replay completed (naturally, or via the grace-period
//     fallback for a game that never signals it) — safe to request a snapshot.
//   - 'stopped': the student used Stop mid-replay — an aborted attempt; the
//     caller must NOT request a snapshot or POST a result (decisions.md).
//   - 'error': an unhandled exception, or the 15s watchdog fired — a run
//     failure; the caller shows the friendly failure message. Also carries
//     `cause: 'student-error' | 'runner-error' | 'timeout'` (additive).
export function replay(core, mode, recordedInputs, challengeSeed) {
  return new Promise(function (resolve) {
    var finished = false;
    var started = false;        // true once the fresh worker reports `running`
    var timers = [];
    var bootWatchdog = null;
    var stateHandler = null;

    function cleanup() {
      timers.forEach(clearTimeout);
      if (bootWatchdog) { clearTimeout(bootWatchdog); bootWatchdog = null; }
      if (stateHandler) { core.off('state', stateHandler); }
    }
    // checker-diagnostics B2: an 'error' outcome also carries an additive
    // `cause` ('student-error' | 'runner-error' | 'timeout') so a caller can
    // tell a student crash from a runner failure or a watchdog. Callers that
    // read only `outcome` are unaffected.
    function finish(outcome, cause) {
      if (finished) { return; }
      finished = true;
      cleanup();
      var result = { outcome: outcome };
      if (cause) { result.cause = cause; }
      resolve(result);
    }

    var events = recordedInputs || [];
    var lastAtMs = 0;
    events.forEach(function (rec) { lastAtMs = Math.max(lastAtMs, rec.atMs || 0); });

    // Schedule the replay itself — input dispatch, the grace-period `finished`
    // fallback, and the no-terminal-state watchdog — ONLY once the fresh worker
    // reports `running`. A Check spins up a COLD worker whose multi-second
    // Pyodide+pygame boot must NOT count against these timers: anchoring them to
    // replay() invocation (as before) let the grace timer elapse mid-boot, so
    // core.stop()+snapshot ran before the program's top level (and, for pgzero,
    // pgzero.runner registering sys.modules['main']) had executed — the snapshot
    // then read an empty module and reported every checkpoint "not found".
    // Anchoring to `running` guarantees the program (and its variables) exist,
    // and that replayed inputs actually land (sendInput no-ops until running).
    function beginReplayPhase() {
      if (started) { return; }
      started = true;
      if (bootWatchdog) { clearTimeout(bootWatchdog); bootWatchdog = null; }

      // Watchdog: a replay that never reaches a terminal state (after the program
      // is running) is treated as a run failure. Widened to WATCHDOG_MS +
      // REPLAY_GRACE_MS (rather than WATCHDOG_MS alone) so a recording whose last
      // event lands right at the shared 15s recording cap still resolves cleanly
      // via the grace-period fallback FIRST — the recording and replay watchdogs
      // share the same constant, so without this widening an answer key recorded
      // right up to the cap would lose the race against its own grace fallback.
      timers.push(setTimeout(function () {
        if (finished) { return; }
        core.stop();
        // Belt-and-braces: force the outcome even if `stopped` never arrives.
        timers.push(setTimeout(function () { finish('error', 'timeout'); }, 2000));
      }, WATCHDOG_MS + REPLAY_GRACE_MS));

      // Grace-period fallback: for a game that never signals `finished` on its
      // own (pgzero always; plain Pygame often), the replay is considered done a
      // short fixed period after the last recorded event's atMs — regardless of
      // the game's own lifecycle (decisions.md task 2.5). A console script or a
      // Pygame program that DOES finish naturally resolves via `stateHandler`
      // first; this is a no-op then (the `finished` guard).
      //
      // code-challenge-expected-output §E: the grace fallback is GATED on
      // `mode !== 'python'`. A console (`python`) program that keeps printing
      // past the grace window (or runs >600ms with no stdin) must NOT be resolved
      // early by this timer — that would `core.stop()` it mid-print and truncate
      // the student's stdout, failing an otherwise-correct expected-output check.
      // Console runs emit a real `'finished'` (resolved via `stateHandler`); the
      // widened watchdog above remains the hang backstop, so nothing hangs.
      if (mode !== 'python') {
        timers.push(setTimeout(function () { finish('finished'); }, lastAtMs + REPLAY_GRACE_MS));
      }

      if (mode === 'python') {
        var frame = core.getRunnerFrame();
        events.forEach(function (rec) {
          timers.push(setTimeout(function () {
            if (finished || !frame.contentWindow) { return; }
            frame.contentWindow.postMessage({ type: 'runner:stdin', text: rec.text }, '*');
          }, Math.max(0, rec.atMs || 0)));
        });
      } else {
        events.forEach(function (rec) {
          timers.push(setTimeout(function () {
            if (finished) { return; }
            var rc = runnerCoreOf(core);
            if (rc && rc.injectInput) {
              var copy = {};
              Object.keys(rec).forEach(function (k) { if (k !== 'atMs') { copy[k] = rec[k]; } });
              rc.injectInput(copy);
            }
          }, Math.max(0, rec.atMs || 0)));
        });
      }
    }

    stateHandler = function (state) {
      if (state === 'running') { beginReplayPhase(); }
      else if (state === 'finished') { finish('finished'); }
      else if (state === 'student-error' || state === 'runner-error') { finish('error', state); }
      else if (state === 'stopped') { finish('stopped'); }
    };
    core.on('state', stateHandler);

    // Boot watchdog: if the fresh worker never reaches `running` (a Pyodide boot
    // hang/failure), fail rather than hang forever. Cleared the instant the
    // replay phase begins.
    bootWatchdog = setTimeout(function () {
      if (finished || started) { return; }
      core.stop();
      timers.push(setTimeout(function () { finish('error', 'timeout'); }, 2000));
    }, WATCHDOG_MS);

    core.runWithChallengeSeed(challengeSeed);
  });
}
